import React from 'react';
const HighlightedText = ({
  children
}) => <span className="text-emerald-500 font-bold" data-id="element-336">{children}</span>;
export function Story() {
  return <section className="py-12" data-id="element-337">
      <div className="max-w-3xl mx-auto" data-id="element-338">
        <p className="text-xl font-bold mb-6" data-id="element-339">
          It would start with this strange tightness in my chest. I was barely
          holding it together at home.
        </p>
        <p className="text-xl mb-6" data-id="element-340">
          Not pain exactly, but a pressure. Like my body was bracing for
          something I couldn't see.
        </p>
        <p className="text-xl mb-6" data-id="element-341">
          Then the rest would follow. A racing heart. Shallow breathing.
          Thoughts spiraling faster than I could catch them.
        </p>
        <p className="text-xl mb-6" data-id="element-342">
          No matter how many times I told myself I was safe… No matter how many
          breathing exercises I tried…
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-center text-green-800 my-8" data-id="element-343">
          My body was already halfway into{' '}
          <HighlightedText data-id="element-344">panic mode</HighlightedText> before I even realized
          it.
        </h2>
        <div className="my-8 flex justify-center" data-id="element-345">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4h5SJfgv5cnCNDsoHRqxcu/All_About_Panic_Attacks_Panic_Disorder1708004687.webp" alt="Person experiencing anxiety and panic" className="rounded-lg shadow-lg w-[65%]" data-id="element-346" />
        </div>
        <p className="text-xl mb-6" data-id="element-347">
          And the worst part? I didn't know how to stop it.
        </p>
        <p className="text-xl mb-6" data-id="element-348">
          I thought maybe it was just modern life. Too much stress. Too many
          tabs open. Too many things to do.
        </p>
        <p className="text-xl mb-6" data-id="element-349">
          But deep down, I knew something was off. I didn't feel resilient
          anymore.
        </p>
        <p className="text-xl font-bold mb-6" data-id="element-350">
          My wife had started pulling away.
        </p>
        <p className="text-xl mb-6" data-id="element-351">
          Saying she felt like she was walking on eggshells. One more outburst,
          one more shutdown, and I was scared she might actually leave.
        </p>
        <p className="text-xl mb-6" data-id="element-352">
          I needed to find something that worked. Not next year. Not in theory.
          Now.
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-center text-green-800 my-8" data-id="element-353">
          Every little thing — a loud noise, a strange sensation, even just
          feeling tired — could throw me off for hours.
        </h2>
        <p className="text-xl mb-6" data-id="element-354">
          And nothing I tried seemed to help for long.
        </p>
        <p className="text-xl mb-6" data-id="element-355">
          I changed my diet. I meditated. I took all the calming supplements the
          internet recommended.
        </p>
        <p className="text-xl mb-6" data-id="element-356">Some helped a little. Most didn't.</p>
        <p className="text-xl mb-6" data-id="element-357">
          But I never felt fully rested. Or present. Or clear.
        </p>
        <p className="text-xl mb-6" data-id="element-358">
          Until I came across something that connected all the dots.
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-center text-green-800 my-8" data-id="element-359">
          "Maybe This Isn't In My Mind… Maybe My{' '}
          <HighlightedText data-id="element-360">Nervous System</HighlightedText> Is Just Misfiring"
        </h2>
        <p className="text-xl mb-6" data-id="element-361">
          I was listening to a podcast while on my daily walk. The guest was a
          trauma expert. And the way he described stress hit me like a lightning
          bolt.
        </p>
        <p className="text-xl mb-6" data-id="element-362">
          He said the body doesn't just react to stress. Sometimes, the body
          gets stuck in stress. Even when the danger is long gone.
        </p>
        <p className="text-xl font-bold mb-6" data-id="element-363">
          He called it a "<HighlightedText data-id="element-364">nervous system</HighlightedText>{' '}
          misfire."
        </p>
        <p className="text-xl mb-6" data-id="element-365">
          The body gets stuck in fight-or-flight. The alarm system keeps
          sounding. Even when there's nothing to run from.
        </p>
        <p className="text-xl mb-6" data-id="element-366">
          That's when the spiral starts. That's why the breathing changes.
          That's why it feels like panic hits from nowhere.
        </p>
        <p className="text-xl mb-6" data-id="element-367">
          And the answer, he said, wasn't another mindset trick. It was
          resetting the system itself.
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-center text-green-800 my-8" data-id="element-368">
          That's When He Mentioned <HighlightedText data-id="element-369">Ayahuasca</HighlightedText>.
        </h2>
        <div className="my-8" data-id="element-370">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aEhWy27PrmvEpJFPxKpiDS/Ayahuasca-Uses-Experience-Effects-and-Dangers.webp" alt="Traditional Ayahuasca ceremony in the Amazon" className="mx-auto rounded-lg shadow-lg max-w-full" data-id="element-371" />
          <p className="text-sm text-center text-gray-600 mt-2" data-id="element-372">
            Traditional Ayahuasca ceremony with indigenous healers
          </p>
        </div>
        <p className="text-xl mb-6" data-id="element-373">
          He started talking about the ceremony that's helped thousands of
          people reconnect with themselves, regulate their emotions, and heal
          old wounds.
        </p>
        <p className="text-xl mb-6" data-id="element-374">
          He said the full ceremony, guided by trained facilitators, could help
          people reset their nervous systems in a deep, lasting way.
        </p>
        <p className="text-xl mb-6" data-id="element-375">But it came with intensity.</p>
        <p className="text-xl mb-6" data-id="element-376">
          Fasting. Purging. Emotional overwhelm. Travel. Time away from work and
          family. It wasn't for everyone.
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-center text-green-800 my-8" data-id="element-377">
          I Wanted In. But I Couldn't Drop Everything and Fly to the Jungle.
        </h2>
        <p className="text-xl mb-6" data-id="element-378">I've got a business. A wife. A toddler.</p>
        <p className="text-xl mb-6" data-id="element-379">
          I don't have a month to detox, fly to the rainforest, and purge for 6
          hours in a circle of strangers.
        </p>
        <p className="text-xl mb-6" data-id="element-380">
          But I also didn't want to ignore the pull.
        </p>
        <p className="text-xl mb-6" data-id="element-381">
          Something in me knew this plant could help.
        </p>
        <p className="text-xl mb-6" data-id="element-382">
          And that's when the guest dropped something that made me sit up
          straight:
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-center text-green-800 my-8" data-id="element-383">
          You Can <HighlightedText data-id="element-384">Microdose</HighlightedText> The Vine Itself &
          Experience The Same Healing Power.
        </h2>
        <div className="my-8" data-id="element-385">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qf3y22D9FauHcopVQ5kpna/ChatGPT_Image_Jun_27%2C_2025%2C_11_31_36_AM.png" alt="Colorful ayahuasca-inspired art" className="mx-auto rounded-lg shadow-lg max-w-full" data-id="element-386" />
          <p className="text-sm text-center text-gray-600 mt-2" data-id="element-387">
            The healing energy of the sacred vine
          </p>
        </div>
        <p className="text-xl mb-6" data-id="element-388">
          Turns out, <HighlightedText data-id="element-389">Ayahuasca</HighlightedText> is made from
          two plants:
        </p>
        <p className="text-xl mb-6" data-id="element-390">
          A leaf that contains the part that causes the visions.
        </p>
        <p className="text-xl mb-6" data-id="element-391">
          A vine called Banisteriopsis caapi (the part that regulates the
          <HighlightedText data-id="element-392"> nervous system</HighlightedText>)
        </p>
        <p className="text-xl font-bold mb-6" data-id="element-393">
          The vine is the medicine. The leaf is just the doorway.
        </p>
        <p className="text-xl mb-6" data-id="element-394">
          The vine alone — without the visions — contains natural harmala
          alkaloids, which act as reversible MAO-A inhibitors.
        </p>
        <p className="text-xl mb-6" data-id="element-395">
          These compounds help regulate serotonin, dopamine, and other key
          neurotransmitters linked to emotional stability and resilience.
        </p>
        <p className="text-xl mb-6" data-id="element-396">
          Studies also suggest Banisteriopsis caapi may promote BDNF
          (Brain-Derived Neurotrophic Factor), which supports neuroplasticity
          and healthy <HighlightedText data-id="element-397">nervous system</HighlightedText>{' '}
          function.
        </p>
        <p className="text-xl mb-6" data-id="element-398">
          Together, these effects can support emotional regulation, serotonin
          balance, and deep <HighlightedText data-id="element-399">nervous system</HighlightedText>{' '}
          repair.
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-center text-green-800 my-8" data-id="element-400">
          Taken in micro amounts it doesn't cause a psychedelic experience. Just
          a subtle shift. A deep calm. A slow reset:{' '}
          <HighlightedText data-id="element-401">Microhuasca</HighlightedText>™
        </h2>
        <div className="my-8" data-id="element-402">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tZAYVZd2Vhqf9wfsbXYPH8/ChatGPT_Image_Jun_16%2C_2025%2C_10_35_34_AM.png" alt="Man holding Aya Caps bottle" className="mx-auto rounded-lg shadow-lg max-w-full" data-id="element-403" />
          <p className="text-sm text-center text-gray-600 mt-2" data-id="element-404">
            Aya Caps brings the healing power of the vine to your daily routine
          </p>
        </div>
        <p className="text-xl mb-6" data-id="element-405">
          A new way to work with the vine. Without the trip. Without the purge.
          Without the flight to Peru.
        </p>
        <p className="text-xl mb-6" data-id="element-406">A simple capsule you can take from home.</p>
        <p className="text-xl mb-6" data-id="element-407">And the brand he trusted?</p>
        <p className="text-xl font-bold mb-6" data-id="element-408">
          <HighlightedText data-id="element-409">Aya Caps</HighlightedText>.
        </p>
      </div>
    </section>;
}